import React from "react";
import { usePayment } from "../contexts/PaymentContext";

/**
 * Visible surface for PaymentContext.errorMessage.
 *
 * Every checkout failure path calls setErrorMessage(), but until this component
 * existed nothing ever RENDERED it — a failed payment or fulfillment setup left
 * the customer staring at a spinner with no explanation (the "Apple Pay spun
 * and never stopped" support report). Rendered fixed and above the confirmation
 * modal overlay so the message is visible no matter where in the flow it fires.
 */
const PaymentErrorToast: React.FC = () => {
  const { errorMessage, setErrorMessage } = usePayment();

  if (!errorMessage) return null;

  return (
    <div
      role="alert"
      className="fixed top-4 left-1/2 -translate-x-1/2 z-[10000] w-[calc(100%-2rem)] max-w-xl"
    >
      <div className="flex items-start gap-3 rounded-lg border border-red-300 bg-red-50 px-4 py-3 shadow-lg">
        <svg
          className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-600"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
        <div className="flex-1">
          <p className="text-sm font-semibold text-red-800">
            There was a problem with your order
          </p>
          <p className="mt-0.5 text-sm text-red-700 break-words">
            {errorMessage}
          </p>
        </div>
        <button
          type="button"
          aria-label="Dismiss error"
          onClick={() => setErrorMessage(null)}
          className="text-red-500 hover:text-red-700 text-xl leading-none font-bold"
        >
          ×
        </button>
      </div>
    </div>
  );
};

export default PaymentErrorToast;
